import { useCallback, useEffect, useState } from 'react';

import type { StoryGroup } from '@nutralab/api';

import { getImagePath } from '@/shared/utils';

const STORAGE_KEY = 'stories_viewed';

function readViewed(): Set<string> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
        return new Set();
    }
}

function persistViewed(urls: Set<string>): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(urls)));
    } catch {
        return;
    }
}

interface ViewedStories {
    viewed: Set<string>;
    markViewed: (url: string) => void;
}

export function useViewedStories(groups: StoryGroup[]): ViewedStories {
    const [viewed, setViewed] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        const actual = new Set(groups.flatMap((g) => g.media.map((m) => getImagePath(m.url))));
        const stored = readViewed();
        const pruned = new Set(Array.from(stored).filter((url) => actual.has(url)));

        if (pruned.size !== stored.size) {
            persistViewed(pruned);
        }
        setViewed(pruned);
    }, [groups]);

    const markViewed = useCallback((url: string) => {
        setViewed((prev) => {
            if (prev.has(url)) {
                return prev;
            }
            const next = new Set(prev).add(url);
            persistViewed(next);
            return next;
        });
    }, []);

    return { viewed, markViewed };
}
